import { assertNever } from '@signalvote/shared';

/**
 * The dashboard hero's period switcher options (ADR-017). `current_period` reads
 * the next filing deadline; `ytd` is calendar year-to-date; `cycle` ("Campaign
 * to date") spans committee formation → today; `custom` is a user-picked range.
 */
export type PeriodId = 'current_period' | 'ytd' | 'cycle' | 'custom';

export interface PeriodWindowInputs {
  /** "yyyy-MM-dd" in campaign timezone (getCampaignTodayString() — never new Date()). */
  campaignToday: string;
  /** Next deadline's period bounds; null until the deadline/health load resolves. */
  periodStart: string | null;
  periodEnd: string | null;
  /** Committee-formation date from finance settings; null until set. */
  formationDate: string | null;
  /** User-picked custom range. */
  customFrom: string;
  customTo: string;
}

export interface PeriodWindow {
  from: string;
  to: string;
}

/**
 * Pure mapping of a {@link PeriodId} to its inclusive `{ from, to }` date-string
 * window. Decision A (ADR-017):
 * - `current_period` → the next deadline's period when BOTH bounds resolve; else YTD.
 * - `ytd` → Jan 1 of campaignToday's year → campaignToday.
 * - `cycle` → committee-formation date → campaignToday; falls back to YTD when unset.
 * - `custom` → the user-picked range.
 *
 * No side effects — all inputs are explicit so the caller (which uses
 * getCampaignTodayString()) supplies the campaign-timezone "today".
 */
export function computePeriodWindow(
  period: PeriodId,
  inputs: PeriodWindowInputs,
): PeriodWindow {
  const { campaignToday, periodStart, periodEnd, formationDate, customFrom, customTo } = inputs;
  const yearStart = `${campaignToday.slice(0, 4)}-01-01`;
  const ytd: PeriodWindow = { from: yearStart, to: campaignToday };

  switch (period) {
    case 'current_period':
      return periodStart && periodEnd ? { from: periodStart, to: periodEnd } : ytd;
    case 'ytd':
      return ytd;
    case 'cycle':
      return formationDate ? { from: formationDate, to: campaignToday } : ytd;
    case 'custom':
      return { from: customFrom, to: customTo };
    default:
      return assertNever(period);
  }
}
